import { apiFetch } from './client';

const inputsPath = runId => `/chat/live/${encodeURIComponent(runId)}/inputs`;

export async function listLiveChatInputs(runId) {
  const response = await apiFetch(inputsPath(runId), { timeoutMs: 15000 });
  return response.json();
}

export async function sendLiveChatInput(runId, payload, files = []) {
  let body = payload;
  if (files.length) {
    if (payload.mode !== 'queue') throw new Error('Attachments cannot be used for steering');
    body = new FormData();
    body.append('payload', JSON.stringify(payload));
    files.forEach(file => body.append('files', file));
  }
  try {
    const response = await apiFetch(`${inputsPath(runId)}${files.length ? '/media' : ''}`, { method: 'POST', body, timeoutMs: files.length ? 60000 : 15000 });
    return await response.json();
  } catch (error) {
    if (error?.status && error.status < 500) throw error;
    // Read back a lost acknowledgement. Only an explicit user retry may POST the same UUID again.
    try {
      const result = await listLiveChatInputs(runId);
      const receipt = result.inputs.find(input => input.request_id === payload.request_id);
      if (receipt) return receipt;
    } catch {}
    throw error;
  }
}

export function isRecalledInput(input, inputId, revision) {
  return String(input?.id) === String(inputId) && input?.status === 'cancelled'
    && input.reason === 'recalled_by_user' && input.recalled_revision === revision;
}

export async function recallLiveChatInput(runId, inputId, payload) {
  try {
    const response = await apiFetch(`${inputsPath(runId)}/${encodeURIComponent(inputId)}/recall`, {
      method: 'POST', body: payload, timeoutMs: 15000,
    });
    const receipt = await response.json();
    if (isRecalledInput(receipt, inputId, payload.expected_revision)) return receipt;
    throw new Error('Recall was not confirmed');
  } catch (error) {
    if (error?.status && error.status < 500) throw error;
    try {
      const result = await listLiveChatInputs(runId);
      const receipt = result.inputs.find(input => isRecalledInput(input, inputId, payload.expected_revision));
      if (receipt) return receipt;
    } catch {}
    throw error;
  }
}

export async function downloadLiveChatInputAttachment(runId, input, attachment) {
  const expires = Date.parse(input.attachments_expires_at);
  if (Number.isFinite(expires) && expires <= Date.now()) throw new Error('Attachment expired');
  if (!/^[a-f0-9]{32}$/i.test(String(attachment.id || ''))) throw new Error('Invalid attachment ID');
  const path = `${inputsPath(runId)}/${encodeURIComponent(input.id)}/attachments/${attachment.id}`;
  if (attachment.url !== path) throw new Error('Invalid attachment download path');
  const response = await apiFetch(path, { timeoutMs: 60000, redirect: 'error' });
  const blob = await response.blob();
  if (blob.size !== attachment.size) throw new Error('Incomplete attachment download');
  return new File([blob], attachment.name, { type: attachment.content_type || blob.type });
}

export async function cancelLiveChatInput(runId, inputId) {
  const response = await apiFetch(`${inputsPath(runId)}/${encodeURIComponent(inputId)}`, { method: 'DELETE', timeoutMs: 15000 });
  return response.json();
}

export async function promoteLiveChatInput(runId, inputId) {
  const response = await apiFetch(`${inputsPath(runId)}/${encodeURIComponent(inputId)}/promote`, { method: 'POST', timeoutMs: 15000 });
  return response.json();
}

export async function editLiveChatInput(runId, inputId, payload) {
  const response = await apiFetch(`${inputsPath(runId)}/${encodeURIComponent(inputId)}`, { method: 'PATCH', body: payload, timeoutMs: 15000 });
  return response.json();
}
